/**
 * LandingHeader Web Component
 * Handles sticky scroll behavior, menu drawer, search modal,
 * and localization dropdown with complete accessibility and theme editor lifecycle.
 */
class LandingHeader extends HTMLElement {
  constructor() {
    super();

    this.wrapper = this.querySelector('.landing-header-wrapper');
    this.stickyType = this.dataset.stickyType || 'on-scroll-up';
    this.lastScrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Elements
    this.drawer = this.querySelector('.landing-header-drawer');
    this.drawerOpenBtn = this.querySelector('[data-action="open-drawer"]');
    this.drawerCloseBtns = this.querySelectorAll('[data-action="close-drawer"]');

    this.searchModal = this.querySelector('.landing-header-search');
    this.searchOpenBtn = this.querySelector('[data-action="open-search"]');
    this.searchCloseBtns = this.querySelectorAll('[data-action="close-search"]');
    this.searchInput = this.querySelector('.landing-header-search__input');

    this.localization = this.querySelector('.landing-header__localization');
    this.localizationBtn = this.querySelector('[data-action="toggle-localization"]');
    this.localizationItems = this.querySelectorAll('.landing-header__localization-item');
    this.localizationForm = this.querySelector('.landing-header__localization-form');

    // Bound listeners for clean removal
    this.onScrollBound = this.onScroll.bind(this);
    this.onKeydownBound = this.onKeydown.bind(this);
    this.onClickOutsideBound = this.onClickOutside.bind(this);
  }

  connectedCallback() {
    this.initSticky();
    this.initDrawer();
    this.initSearch();
    this.initLocalization();

    document.addEventListener('keydown', this.onKeydownBound);
    document.addEventListener('click', this.onClickOutsideBound);

    // Section events in Shopify Theme Editor
    this.ownerDocument.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    this.ownerDocument.addEventListener('shopify:section:unload', this.onSectionUnload.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollBound);
    document.removeEventListener('keydown', this.onKeydownBound);
    document.removeEventListener('click', this.onClickOutsideBound);
  }

  initSticky() {
    if (this.stickyType === 'none' || !this.wrapper) return;
    this.wrapper.classList.add('landing-header-wrapper--sticky');
    window.addEventListener('scroll', this.onScrollBound, { passive: true });
  }

  onScroll() {
    if (!this.wrapper) return;
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;

    if (this.stickyType === 'always') {
      return;
    }

    if (this.stickyType === 'on-scroll-up') {
      const headerHeight = this.offsetHeight;

      if (currentScrollY > headerHeight + 50) {
        if (currentScrollY > this.lastScrollY) {
          // Scrolling down
          this.wrapper.classList.add('landing-header-wrapper--sticky-hidden');
        } else {
          // Scrolling up
          this.wrapper.classList.remove('landing-header-wrapper--sticky-hidden');
        }
      } else {
        this.wrapper.classList.remove('landing-header-wrapper--sticky-hidden');
      }
    }

    this.lastScrollY = Math.max(0, currentScrollY);
  }

  initDrawer() {
    if (!this.drawer) return;

    if (this.drawerOpenBtn) {
      this.drawerOpenBtn.addEventListener('click', () => this.openDrawer());
    }

    this.drawerCloseBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closeDrawer());
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.drawerOpenBtn) this.drawerOpenBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    const focusable = this.drawer.querySelector('button, a, input');
    if (focusable) focusable.focus();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.drawerOpenBtn) this.drawerOpenBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  initSearch() {
    if (!this.searchModal) return;

    if (this.searchOpenBtn) {
      this.searchOpenBtn.addEventListener('click', () => this.openSearch());
    }

    this.searchCloseBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closeSearch());
    });
  }

  openSearch() {
    if (!this.searchModal) return;
    this.searchModal.classList.add('is-open');
    if (this.searchOpenBtn) this.searchOpenBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 50);
    }
  }

  closeSearch() {
    if (!this.searchModal) return;
    this.searchModal.classList.remove('is-open');
    if (this.searchOpenBtn) this.searchOpenBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  initLocalization() {
    if (!this.localization || !this.localizationBtn) return;

    this.localizationBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = this.localization.classList.toggle('is-open');
      this.localizationBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    this.localizationItems.forEach(item => {
      item.addEventListener('click', () => {
        const countryCode = item.dataset.value;
        if (countryCode && this.localizationForm) {
          const input = this.localizationForm.querySelector('input[name="country_code"]');
          if (input) {
            input.value = countryCode;
            this.localizationForm.submit();
          }
        }
      });
    });
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      if (this.drawer && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
      if (this.searchModal && this.searchModal.classList.contains('is-open')) {
        this.closeSearch();
      }
      if (this.localization && this.localization.classList.contains('is-open')) {
        this.localization.classList.remove('is-open');
        if (this.localizationBtn) this.localizationBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  onClickOutside(e) {
    if (this.localization && this.localization.classList.contains('is-open')) {
      if (!this.localization.contains(e.target)) {
        this.localization.classList.remove('is-open');
        if (this.localizationBtn) this.localizationBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  onSectionLoad(e) {
    if (e.target === this.closest('.shopify-section')) {
      this.initSticky();
    }
  }

  onSectionUnload(e) {
    if (e.target === this.closest('.shopify-section')) {
      window.removeEventListener('scroll', this.onScrollBound);
    }
  }
}

customElements.define('landing-header', LandingHeader);
